import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Trash2, AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { User } from '../types';
import { fetchBookings, Booking, isLifecycleConnected } from '../utils/lifecycleApi';
import { loadFromStorage } from '../utils/helpers';
import { formatCurrency } from '../utils/helpers';

interface CostItem { id: string; category: string; description: string; vendor: string; amount: number; paid: boolean; notes: string; }
interface BookingCost { bookingId: number; items: CostItem[]; }

const COST_CATEGORIES = ['Contractor', 'PP Vendor', 'Album', 'Transportation', 'Venue', 'Equipment', 'Miscellaneous'];

const STORAGE_KEY = 'ss_cost_management_v1';
function loadCosts(): Record<number, CostItem[]> {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}
function saveCosts(data: Record<number, CostItem[]>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function CostManagement({ user }: { user: User }) {
  const [connected] = useState(isLifecycleConnected());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Booking | null>(null);
  const [costs, setCosts] = useState<Record<number, CostItem[]>>(loadCosts());
  const [form, setForm] = useState({ category: COST_CATEGORIES[0], description: '', vendor: '', amount: '', paid: false, notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    try { setBookings(await fetchBookings()); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }, [connected]);

  useEffect(() => { load(); }, [load]);

  // Auto-populate costs from linked quote
  const autoPopulateFromQuote = (bookingId: number) => {
    const links = JSON.parse(localStorage.getItem('ss_booking_quote_links') || '{}');
    const linked = links[bookingId];
    if (!linked) return;
    const quotes = loadFromStorage<any[]>('ss_quotes', []);
    const quote = quotes.find((q: any) => q.quoteNumber === linked.quoteNumber);
    if (!quote) return;
    const pkg = (quote.packageItems || [])[0];
    const existing = costs[bookingId] || [];
    if (existing.length > 0) return; // don't overwrite existing entries
    const newItems: CostItem[] = [];
    // Add contractor costs
    (pkg?.contractors || []).forEach((c: any) => {
      const days = (pkg?.eventDates || []).filter((d: any) => d.date).length || 1;
      const total = c.rate * (c.qty || 1) * days;
      if (total > 0) {
        newItems.push({ id: Date.now() + Math.random() + '', category: 'Contractor', description: c.deliverable, vendor: c.contractor, amount: total, paid: false, notes: `${days} day(s) × ₹${c.rate}/day` });
      }
    });
    // Add PP vendor costs
    (pkg?.ppVendors || []).forEach((v: any) => {
      const total = v.rate * (v.qty || 1);
      if (total > 0) {
        newItems.push({ id: Date.now() + Math.random() + '', category: 'PP Vendor', description: v.deliverable, vendor: v.vendor, amount: total, paid: false, notes: `₹${v.rate}${(v.qty||1)>1?` × ${v.qty}`:''}` });
      }
    });
    if (newItems.length > 0) {
      const updated = { ...costs, [bookingId]: [...existing, ...newItems] };
      setCosts(updated); saveCosts(updated);
    }
  };

  const bookingCosts = selected ? (costs[selected.id] || []) : [];
  const totalCost = bookingCosts.reduce((s, c) => s + c.amount, 0);
  const paidCost = bookingCosts.filter(c => c.paid).reduce((s, c) => s + c.amount, 0);
  const unpaidCost = totalCost - paidCost;

  const addItem = () => {
    if (!selected || !form.description || !form.amount) return;
    const newItem: CostItem = { id: Date.now().toString(), category: form.category, description: form.description, vendor: form.vendor, amount: Number(form.amount), paid: form.paid, notes: form.notes };
    const updated = { ...costs, [selected.id]: [...(costs[selected.id] || []), newItem] };
    setCosts(updated); saveCosts(updated);
    setForm({ category: COST_CATEGORIES[0], description: '', vendor: '', amount: '', paid: false, notes: '' });
  };

  const removeItem = (itemId: string) => {
    if (!selected) return;
    const updated = { ...costs, [selected.id]: (costs[selected.id] || []).filter(c => c.id !== itemId) };
    setCosts(updated); saveCosts(updated);
  };

  const togglePaid = (itemId: string) => {
    if (!selected) return;
    const updated = { ...costs, [selected.id]: (costs[selected.id] || []).map(c => c.id === itemId ? { ...c, paid: !c.paid } : c) };
    setCosts(updated); saveCosts(updated);
  };

  if (!connected) return (
    <div className="page-content">
      <div className="alert alert-error"><WifiOff size={15} /> <span>Not connected to Lifecycle backend. Check Settings.</span></div>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Cost Management</h1><p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Track actual costs per booking — contractor, vendor, album, and other expenses</p></div>
        <button className="btn btn-ghost btn-icon" onClick={load}><RefreshCw size={15} /></button>
      </div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><AlertTriangle size={15} /> <span>{error}</span></div>}

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>
        {/* Booking list */}
        <div>
          <label style={{ fontSize: 12, marginBottom: 8 }}>Select Booking</label>
          {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div> : bookings.map(b => {
            const bc = costs[b.id] || [];
            const total = bc.reduce((s, c) => s + c.amount, 0);
            return (
              <div key={b.id} onClick={() => setSelected(b)} style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', border: `1.5px solid ${selected?.id === b.id ? 'var(--teal)' : 'var(--border)'}`, background: selected?.id === b.id ? 'rgba(56,178,172,0.06)' : '#fff' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{b.customer_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.csr_number} · {b.service_type}</div>
                {total > 0 && <div style={{ fontSize: 12, color: 'var(--gold)', marginTop: 2 }}>{formatCurrency(total)} tracked</div>}
              </div>
            );
          })}
        </div>

        {/* Cost detail */}
        <div>
          {!selected ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Select a booking to track costs</div>
          ) : (
            <>
              <div className="card" style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL COSTS</div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gold)' }}>{formatCurrency(totalCost)}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>PAID</div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--teal)' }}>{formatCurrency(paidCost)}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>OUTSTANDING</div><div style={{ fontSize: 22, fontWeight: 700, color: '#E53E3E' }}>{formatCurrency(unpaidCost)}</div></div>
                </div>
              </div>

              {/* Add item */}
              <div className="card" style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Add Cost Item</div>
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div className="form-group">
                    <label>Category</label>
                    <select className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                      {COST_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description *</label>
                    <input className="input" placeholder="e.g. Gunabh contractor payment" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                  </div>
                </div>
                <div className="form-row" style={{ marginBottom: 8 }}>
                  <div className="form-group">
                    <label>Vendor / Person</label>
                    <input className="input" placeholder="e.g. Gunabh" value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Amount (₹) *</label>
                    <input className="input" type="number" placeholder="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                  </div>
                </div>
                <div className="form-row" style={{ marginBottom: 12 }}>
                  <div className="form-group">
                    <label>Notes</label>
                    <input className="input" placeholder="Optional notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={form.paid} onChange={e => setForm({ ...form, paid: e.target.checked })} />
                      Already paid
                    </label>
                    <button className="btn btn-primary" onClick={addItem} style={{ marginLeft: 'auto' }}><Plus size={14} /> Add</button>
                  </div>
                </div>
              </div>

              {/* Items list */}
              {bookingCosts.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 24 }}>No costs tracked yet. Add items above.</div>
              ) : (
                <div className="card">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Category', 'Description', 'Vendor', 'Amount', 'Status', ''].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {bookingCosts.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 10px' }}><span className="badge badge-amber" style={{ fontSize: 10 }}>{item.category}</span></td>
                          <td style={{ padding: '8px 10px' }}>{item.description}{item.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.notes}</div>}</td>
                          <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{item.vendor || '—'}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 600 }}>{formatCurrency(item.amount)}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <button onClick={() => togglePaid(item.id)} style={{ padding: '2px 8px', borderRadius: 100, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, background: item.paid ? '#D1FAE5' : '#FEE2E2', color: item.paid ? '#065F46' : '#7F1D1D', border: 'none' }}>
                              {item.paid ? '✓ Paid' : '○ Unpaid'}
                            </button>
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            {user.role === 'admin' && <button className="btn btn-ghost btn-icon btn-sm" onClick={() => removeItem(item.id)}><Trash2 size={12} color="var(--red-light)" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
